<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useDialogFocus } from '@ilteducation/components';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/use-locale';
import ConfirmDialog from '../components/host/ConfirmDialog.vue';
import QuizterCard from '../components/QuizterCard.vue';
import QuizterLayout from '../layout/QuizterLayout.vue';
import { useQuizterStore } from '../stores/quizterStore';
import type { Quizter } from '@ilteducation/quizter-types';
import { Menu } from '@ilteducation/components';
import ShareDialog from '../components/host/ShareDialog.vue';

const emit = defineEmits<{
  (e: 'start', resourceIds: string[], activityId: string): void;
  (e: 'edit', activityId: string): void;
  (e: 'back'): void;
}>();

const { xs } = useDisplay();

const { t } = useLocale();
const quizterStore = useQuizterStore();

const quizters = ref<Quizter[]>([]);
const isLoading = ref(true);
const hasError = ref(false);
const startingActivityId = ref<string | null>(null);
const quizterToDelete = ref<Quizter | null>(null);
const deletingActivityId = ref<string | null>(null);
const deleteError = ref(false);

const { rememberFocus } = useDialogFocus([computed(() => !!quizterToDelete.value)]);
const quizterToShare = ref<Quizter | null>(null);

const loadQuizters = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    quizters.value = await quizterStore.fetchMyQuizters();
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadQuizters);

const handleStart = (quizter: Quizter) => {
  if (quizter.resourceIds.length === 0 || startingActivityId.value || deletingActivityId.value) {
    return;
  }
  startingActivityId.value = quizter.activityId;
  emit('start', quizter.resourceIds, quizter.activityId);
};

const handleEdit = (quizter: Quizter) => {
  if (startingActivityId.value) {
    return;
  }
  emit('edit', quizter.activityId);
};

const openShareDialog = (quizter: Quizter) => {
  if (startingActivityId.value || deletingActivityId.value) {
    return;
  }
  rememberFocus();
  quizterToShare.value = quizter;
};

const closeShareDialog = () => {
  quizterToShare.value = null;
};

const openDeleteDialog = (quizter: Quizter) => {
  if (startingActivityId.value || deletingActivityId.value) {
    return;
  }
  rememberFocus();
  deleteError.value = false;
  quizterToDelete.value = quizter;
};

const closeDeleteDialog = () => {
  if (deletingActivityId.value) {
    return;
  }
  quizterToDelete.value = null;
};

const confirmDelete = async () => {
  const quizter = quizterToDelete.value;
  if (!quizter) {
    return;
  }
  deletingActivityId.value = quizter.activityId;
  deleteError.value = false;
  try {
    await quizterStore.deleteQuizter(quizter.activityId);
    quizters.value = quizters.value.filter((q) => q.activityId !== quizter.activityId);
    quizterToDelete.value = null;
  } catch {
    deleteError.value = true;
  } finally {
    deletingActivityId.value = null;
  }
};
</script>

<template>
  <QuizterLayout :show-header="false" :show-back-button="true" back-button-pos="topLeft" @back="emit('back')">
    <div class="d-flex flex-column align-center justify-center ga-12">
      <h1 class="chalkduster-font text-white text-center text-h1">{{ t('my_quizters.title') }}</h1>

      <!-- Loading -->
      <div v-if="isLoading" class="loading d-flex flex-column align-center ga-4">
        <v-progress-circular indeterminate color="white" size="48"></v-progress-circular>
      </div>

      <!-- Error -->
      <div v-else-if="hasError" class="empty-state d-flex flex-column align-center ga-4">
        <v-icon icon="fa:fawsb fa-circle-exclamation" class="opacity-50" size="64" color="white"></v-icon>
        <p class="chalkboard-font text-white text-center text-h6 ma-0 opacity-70">
          {{ t('my_quizters.error') }}
        </p>
        <v-btn variant="outlined" color="white" class="text-none chalkduster-font" @click="loadQuizters">
          {{ t('my_quizters.retry') }}
        </v-btn>
      </div>

      <!-- Empty -->
      <div
        v-else-if="quizters.length === 0"
        class="empty-state d-flex flex-column align-center ga-4"
        data-testid="my-quizters-empty"
      >
        <v-icon icon="fa:fawsb fa-heart" class="opacity-50" size="64" color="white"></v-icon>
        <p class="chalkboard-font text-white text-center text-h6 ma-0 opacity-70">
          {{ t('my_quizters.empty') }}
        </p>
      </div>

      <!-- List -->
      <div v-else class="quizter-list d-flex flex-column ga-4 overflow-y-auto">
        <QuizterCard
          v-for="quizter in quizters"
          :key="quizter.activityId"
          :resource-ids="quizter.resourceIds"
          :name="quizter.name || t('my_quizters.untitled')"
          :description="`${t('my_quizters.questions', { count: quizter.questionsLength })} ${
            quizter.description ? `· ${quizter.description}` : ''
          }`"
          :disabled="startingActivityId === quizter.activityId || deletingActivityId === quizter.activityId"
          :is-starting="startingActivityId === quizter.activityId"
          @start="handleStart(quizter)"
        >
          <template #actions>
            <!-- Action buttons -->
            <div class="quizter-actions d-flex ga-2">
              <!-- Edit button -->
              <v-btn
                icon
                size="small"
                color="white"
                class="edit-quizter-btn"
                variant="text"
                :disabled="startingActivityId === quizter.activityId"
                :aria-label="t('my_quizters.edit')"
                data-testid="edit-quizter-button"
                @click.stop="handleEdit(quizter)"
              >
                <v-icon icon="fa:fawsb fa-pencil" size="18"></v-icon>
                <v-tooltip activator="parent" location="top" aria-hidden="true">
                  <span>{{ t('my_quizters.edit') }}</span>
                </v-tooltip>
              </v-btn>

              <!-- Menu with more options -->
              <v-tooltip location="top" :aria-hidden="true" :disabled="xs">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    variant="text"
                    color="white"
                    size="small"
                    :aria-label="t('components.label_additional')"
                  >
                    <v-icon icon="fa:fawsb fa-ellipsis" size="18"></v-icon>
                    <Menu :open-on-click="true" :title="t('components.label_advanced_menu')">
                      <!-- Share option -->
                      <v-list-item @click="openShareDialog(quizter)" data-testid="my-quizter-share">
                        <v-list-item-title class="menu-item-title">
                          <span class="menu-item-icon">
                            <v-icon icon="fa:fas fa-share-from-square" size="18"></v-icon>
                          </span>
                          {{ t('my_quizters.share') }}
                        </v-list-item-title>
                      </v-list-item>

                      <!-- Delete option -->
                      <v-list-item @click="openDeleteDialog(quizter)" data-testid="my-quizter-delete">
                        <v-list-item-title class="menu-item-title">
                          <span class="menu-item-icon">
                            <v-icon icon="fa:fas fa-trash" size="18"></v-icon>
                          </span>
                          {{ t('my_quizters.delete') }}
                        </v-list-item-title>
                      </v-list-item>
                    </Menu>
                  </v-btn>
                </template>
                {{ t('components.label_additional') }}
              </v-tooltip>
            </div>
          </template>
        </QuizterCard>
      </div>
    </div>

    <ConfirmDialog
      :model-value="!!quizterToDelete"
      :title="t('my_quizters.delete_confirm_title')"
      :message="
        t('my_quizters.delete_confirm_message', {
          name: quizterToDelete?.name || t('my_quizters.untitled'),
        })
      "
      :confirm-label="t('my_quizters.delete_confirm_button')"
      :cancel-label="t('my_quizters.delete_cancel_button')"
      :error-message="t('my_quizters.delete_error')"
      :error="deleteError"
      :loading="!!deletingActivityId"
      confirm-color="error"
      @update:model-value="closeDeleteDialog"
      @confirm="confirmDelete"
    />

    <ShareDialog
      v-if="!!quizterToShare"
      :item-to-share="quizterToShare"
      :title="t('my_quizters.share')"
      @close="closeShareDialog"
    ></ShareDialog>
  </QuizterLayout>
</template>

<style scoped lang="scss">
.empty-state {
  min-height: 200px;
  max-width: 500px;
}

.loading {
  min-height: 200px;
}

.quizter-list {
  width: min(600px, calc(100vw - 3rem));
  // Keep the list inside its own scroll area so the whole page doesn't scroll.
  max-height: 60vh;
  // Breathing room so the hover scale and icon outline aren't clipped by the scroll area.
  padding: 0.5rem;

  // Custom scrollbar styling to match the Quizter theme (same as VideoList / AddVideoDialog).
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.5);
  }
}

// Action buttons stacked in a row in the top-right corner of the card.
.quizter-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.menu-item-title {
  display: flex;
  align-items: center;
}

.menu-item-icon {
  display: inline-flex;
  justify-content: center;
  width: 20px;
  margin-right: 12px;
}
</style>
